import type { Service } from '~publish/components/Composer/graphqlEnums'
import type { ChannelNavChannel } from '~publish/components/PublishSidebar/useChannels'

import type { JSX } from 'react'

export function hasMultipleChannelTypeRestrictions(
  channels: ChannelNavChannel[],
): Service[] | null {
  const restrictions: Service[] = []

  // Check for 2 or more Twitter/X channels
  const twitterChannels = channels.filter(
    (channel) => channel.service === 'twitter',
  )
  if (twitterChannels.length >= 2) {
    restrictions.push('twitter')
  }

  // Check for both a Facebook Page and a Facebook Group
  const hasFacebookPage = channels.some(
    (channel) => channel.service === 'facebook' && channel.type === 'page',
  )
  const hasFacebookGroup = channels.some(
    (channel) => channel.service === 'facebook' && channel.type === 'group',
  )
  if (hasFacebookPage && hasFacebookGroup) {
    restrictions.push('facebook')
  }

  // Check for Instagram business + personal profiles
  const instagramBusiness = channels.filter(
    (channel) => channel.service === 'instagram' && channel.type === 'business',
  )
  const instagramPersonal = channels.filter(
    (channel) => channel.service === 'instagram' && channel.type === 'profile',
  )
  if (instagramBusiness.length > 0 && instagramPersonal.length > 0) {
    restrictions.push('instagram')
  }

  return restrictions.length > 0 ? restrictions : null
}

export function getMultipleChannelTypeRestrictionWarning(
  restrictions: Service[],
): JSX.Element {
  const hasMoreThanOneRestriction = restrictions.length > 1

  if (hasMoreThanOneRestriction) {
    return <>You&apos;ll only be able to post to one channel type at a time.</>
  }

  switch (restrictions[0]) {
    case 'twitter':
      return (
        <>
          You&apos;ll only be able to post to one Twitter/X channel at a time.
        </>
      )
    case 'facebook':
      return (
        <>
          You&apos;ll only be able to post to one Facebook channel type <br />
          at a time.
        </>
      )
    case 'instagram':
      return (
        <>
          You&apos;ll only be able to post to one Instagram business or personal
          profile at a time.
        </>
      )
    default:
      return (
        <>You&apos;ll only be able to post to one channel type at a time.</>
      )
  }
}
